<script setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { focusForTyping, rememberTappedBlank } from "@/utils/keyboard";
import { answersByKey, blankKey, sameBlank } from "@/utils/blanks";

// One line of a make: plain, bold and italic words, and {{blanks}} to fill in,
// highlighted the same way as sentences picked for an edit
const props = defineProps({
  runs: { type: Array, required: true },
  fillable: { type: Boolean, default: false },
  blankValues: { type: Object, default: () => ({}) },
});
const emit = defineEmits(["blank"]);

// Every copy of a blank shows its answer, however it was written
const answers = computed(() => answersByKey(props.blankValues));
const answer = (run) => answers.value.get(blankKey(run.blank)) || "";
// The blank being filled in stands out
const store = useStore();
const filling = computed(() => store.state.result.fillingBlank);

// Focusing the box during the tap is what lets a phone open its keyboard straight away
const pick = (blank, event) => {
  focusForTyping("blank-filler-input");
  rememberTappedBlank(event.currentTarget);
  emit("blank", blank);
};
</script>

<template>
  <template v-for="(run, i) in runs" :key="i">
    <component
      :is="fillable ? 'button' : 'span'"
      v-if="run.blank !== undefined"
      :type="fillable ? 'button' : undefined"
      class="rounded-lg px-1 transition-colors duration-200 [overflow-wrap:anywhere]"
      :class="[
        // Filled in, a faint wash of the brand purple so answers blend back in
        answer(run)
          ? [
              'bg-accentPrimary/[0.12]',
              { 'cursor-pointer hover:bg-accentPrimary/25': fillable },
            ]
          : [
              'bg-yellow-300 bg-opacity-90',
              { 'cursor-pointer hover:bg-opacity-70': fillable },
            ],
        {
          'font-bold': run.bold,
          italic: run.italic || !answer(run),
          'ring-2 ring-accentPrimary ring-offset-1':
            fillable && filling && sameBlank(filling, run.blank),
        },
      ]"
      :data-blank="run.blank"
      :data-blank-key="blankKey(run.blank)"
      :title="fillable ? `Fill in: ${run.blank}` : run.blank"
      @mousedown="fillable && $event.preventDefault()"
      @click="fillable && pick(run.blank, $event)"
      >{{ answer(run) || run.blank }}</component
    >
    <span v-else :class="{ 'font-bold': run.bold, italic: run.italic }">{{
      run.text
    }}</span>
  </template>
</template>
